import { Input } from "@mantine/core"
import { Popover } from "@shopify/polaris"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { CoolButton } from "../CoolButton.jsx"
import { GlowingButtonContainer } from "../GlowingButtonContainer.jsx"
import { AIStateContext } from "../providers/AIStateProvider.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { useTranslation } from "react-i18next"
import { getLanguageName, LANGUAGE_CODES } from "../../utils/languages.js"

export const AIOrdersButtons = ({ size, inputType, input, disabled }) => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { aiState, setAIState } = useContext(AIStateContext)
	const navigate = useNavigate()
	const { t, i18n } = useTranslation()

	const [subscriptionPopoverActive, setSubscriptionPopoverActive] =
		useState(false)

	const [translatePopoverActive, setTranslatePopoverActive] = useState(false)
	const [selectedLanguage, setSelectedLanguage] = useState(null)

	const [languageSearchInput, setLanguageSearchInput] = useState("")

	const addRewordAIOrder = () => {
		if (disabled) {
			setSubscriptionPopoverActive(true)
			return
		}

		const taskAlreadyAdded = aiState.aiOrders.find(
			(order) => order.task === `reword:${inputType}`,
		)

		if (!taskAlreadyAdded) {
			setAIState({
				aiOrders: [
					...aiState.aiOrders,
					{
						task: `reword:${inputType}`,
					},
				],
			})
		} else {
			setAIState({
				aiOrders: aiState.aiOrders.filter(
					(order) => order.task !== `reword:${inputType}`,
				),
			})
		}
	}

	const addTranslateAIOrder = async (lang) => {
		if (disabled) {
			setSubscriptionPopoverActive(true)
			return
		}

		const taskAlreadyAdded = aiState.aiOrders.find(
			(order) => order.task === `translate:${inputType}`,
		)

		if (taskAlreadyAdded && taskAlreadyAdded.targetLang !== lang) {
			setAIState({
				aiOrders: aiState.aiOrders.map((order) => {
					if (order.task === `translate:${inputType}`) {
						return {
							...order,
							targetLang: lang,
						}
					} else {
						return order
					}
				}),
			})
		} else if (taskAlreadyAdded) {
			setAIState({
				aiOrders: aiState.aiOrders.filter(
					(order) => order.task !== `translate:${inputType}`,
				),
			})
		} else if (!taskAlreadyAdded) {
			setAIState({
				aiOrders: [
					...aiState.aiOrders,
					{
						task: `translate:${inputType}`,
						targetLang: lang,
					},
				],
			})
		}
	}

	const toggleTranslatePopover = (e) => {
		e.stopPropagation()
		setTranslatePopoverActive(!translatePopoverActive)
	}

	const changeSelectedLanguage = (lang) => {
		setSelectedLanguage(lang)
		setTranslatePopoverActive(false)
		addTranslateAIOrder(lang)
	}

	const filterLanguageCodes = (languageCodes, searchInput) => {
		if (!searchInput) return languageCodes

		return languageCodes.filter((lang) => {
			return LANGUAGE_CODES[lang]
				.toLowerCase()
				.includes(searchInput.toLowerCase())
		})
	}

	useEffect(() => {
		if (state.storeLanguage || state.defaultTranslateLanguage) {
			setSelectedLanguage(
				state.defaultTranslateLanguage || state.storeLanguage,
			)
		}
	}, [state.storeLanguage, state.defaultTranslateLanguage])

	const successfulCheck = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size === "small" ? "0.9rem" : "1rem"}
			height={size === "small" ? "0.9rem" : "1rem"}
			viewBox="0 0 24 24"
		>
			<path
				fill="currentColor"
				d="M9 16.17L5.53 12.7a.996.996 0 1 0-1.41 1.41l4.18 4.18c.39.39 1.02.39 1.41 0L20.29 7.71a.996.996 0 1 0-1.41-1.41L9 16.17z"
			></path>
		</svg>
	)

	const isTranslateChecked = aiState.aiOrders.find(
		(order) => order.task === `translate:${inputType}`,
	)
	const isRewordChecked = aiState.aiOrders.find(
		(order) => order.task === `reword:${inputType}`,
	)
	const languageCodes = Object.keys(LANGUAGE_CODES)

	const filteredLanguageCodes = useMemo(
		() => filterLanguageCodes(languageCodes, languageSearchInput),
		[languageCodes, languageSearchInput],
	)

	return (
		<div style={{ display: "flex", alignItems: "center" }}>
			{inputType !== "variants" && (
				<div
					onClick={addRewordAIOrder}
					style={{
						background: "#e8c2ff",
						color: "#7415ac",
						borderRadius: "0.3rem",
						display: "flex",
						gap: "0.2rem",
						fontSize: size === "small" ? "0.75rem" : "0.85rem",
						cursor: "pointer",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							padding:
								size === "small"
									? "0.1rem 0.3rem"
									: "0.3rem 0.5rem",
							paddingRight:
								size === "small" ? "0.2rem" : "0.4rem",
							borderRadius: "0.3rem 0rem 0rem 0.3rem",
							background: isRewordChecked
								? "#dda6ff"
								: "transparent",
						}}
					>
						{isRewordChecked ? (
							successfulCheck
						) : (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width={size === "small" ? "0.9rem" : "1rem"}
								height={size === "small" ? "0.8rem" : "1rem"}
								viewBox="0 0 15 15"
							>
								<path
									fill="currentColor"
									fillRule="evenodd"
									d="M13.9.5a.4.4 0 0 0-.8 0v.6h-.6a.4.4 0 0 0 0 .8h.6v.6a.4.4 0 0 0 .8 0v-.6h.6a.4.4 0 0 0 0-.8h-.6V.5Zm-2.046 2.646a.5.5 0 0 1 0 .708l-1 1a.5.5 0 1 1-.707-.708l1-1a.5.5 0 0 1 .707 0Zm-2 2a.5.5 0 0 1 0 .708l-7 7a.5.5 0 1 1-.708-.708l7-7a.5.5 0 0 1 .708 0ZM13.5 5.1c.22 0 .4.18.4.4v.6h.6a.4.4 0 0 1 0 .8h-.6v.6a.4.4 0 0 1-.8 0v-.6h-.6a.4.4 0 0 1 0-.8h.6v-.6c0-.22.18-.4.4-.4ZM8.9.5a.4.4 0 0 0-.8 0v.6h-.6a.4.4 0 1 0 0 .8h.6v.6a.4.4 0 1 0 .8 0v-.6h.6a.4.4 0 0 0 0-.8h-.6V.5Z"
									clipRule="evenodd"
								></path>
							</svg>
						)}
					</div>
					<div
						style={{
							padding:
								size === "small"
									? "0.1rem 0.3rem"
									: "0.3rem 0.5rem",
							paddingLeft: "0.1rem",
						}}
					>
						<span>
							{t("ai.enhance_with")}{" "}
							<span style={{ fontWeight: "600" }}>AI</span>
						</span>
					</div>
				</div>
			)}

			{inputType !== "variants" && (
				<span
					style={{
						fontSize: size === "small" ? "0.8rem" : "0.9rem",
						marginLeft: "0.5rem",
						marginRight: "0.5rem",
					}}
				>
					or
				</span>
			)}

			<div
				onClick={() => addTranslateAIOrder(selectedLanguage)}
				style={{
					background: "#c2e1ff",
					color: "#154cac",
					borderRadius: "0.3rem",
					display: "flex",
					gap: "0.2rem",
					fontSize: size === "small" ? "0.75rem" : "0.85rem",
					cursor: "pointer",
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						padding:
							size === "small"
								? "0.1rem 0.3rem"
								: "0.3rem 0.5rem",
						paddingRight: size === "small" ? "0.1rem" : "0.4rem",
						borderRadius: "0.3rem 0rem 0rem 0.3rem",
						background: isTranslateChecked
							? "#a7d4ff"
							: "transparent",
					}}
				>
					{isTranslateChecked ? (
						successfulCheck
					) : (
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width={size === "small" ? "0.9rem" : "1rem"}
							height={size === "small" ? "0.9rem" : "1rem"}
							viewBox="0 0 32 32"
						>
							<path
								fill="currentColor"
								d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
							></path>
						</svg>
					)}
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.2rem",
						padding:
							size === "small"
								? "0.1rem 0.3rem"
								: "0.3rem 0.5rem",
						paddingLeft: "0rem",
					}}
				>
					<span>
						{t("ai.translate_to")}{" "}
						<span
							style={{
								fontWeight: "600",
								textTransform: "capitalize",
							}}
						>
							{getLanguageName(selectedLanguage, i18n.language)}
						</span>
					</span>
					<div
						className="language-selector-icon"
						style={{
							height: size === "small" ? "0.85rem" : "0.95rem",
							width: size === "small" ? "0.85rem" : "0.95rem",
						}}
						onClick={(e) => toggleTranslatePopover(e)}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width={size === "small" ? "0.9rem" : "1rem"}
							height={size === "small" ? "0.9rem" : "1rem"}
							viewBox="0 0 512 512"
						>
							<path
								d="M128 192l128 128 128-128z"
								fill="currentColor"
							></path>
						</svg>
					</div>
				</div>
			</div>

			<Popover
				active={translatePopoverActive}
				activator={<div></div>}
				onClose={() => setTranslatePopoverActive(false)}
			>
				<div
					style={{
						padding: "0.5rem",
						height: "12rem",
						width: "9rem",
						overflowY: "scroll",
					}}
				>
					<p style={{ fontSize: "0.7rem", marginBottom: "0.5rem" }}>
						{t("ai.your_store_languages")}
					</p>

					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "0.1rem",
						}}
					>
						{state.availableStoreLanguages.map((lang) => {
							return (
								<div
									key={lang}
									onClick={() => changeSelectedLanguage(lang)}
									className="language-button"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="0.85rem"
										height="0.85rem"
										viewBox="0 0 32 32"
									>
										<path
											fill="currentColor"
											d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
										></path>
									</svg>
									<span
										style={{
											fontSize: "0.85rem",
											textTransform: "capitalize",
										}}
									>
										{getLanguageName(lang, i18n.language)}
									</span>
								</div>
							)
						})}
					</div>

					<div
						style={{
							borderTop: "1px solid #ccc",
							paddingTop: "1rem",
							marginTop: "0.5rem",
						}}
					>
						<p
							style={{
								fontSize: "0.7rem",
								marginBottom: "0.5rem",
							}}
						>
							{t("ai.all_languages")}
						</p>

						<Input
							variant="filled"
							size="xs"
							radius="md"
							placeholder={t("ai.search_language")}
							value={languageSearchInput}
							onChange={(e) =>
								setLanguageSearchInput(e.target.value)
							}
						/>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "0.1rem",
								marginTop: "0.5rem",
							}}
						>
							{filteredLanguageCodes.map((lang) => {
								return (
									<div
										key={lang}
										onClick={() =>
											changeSelectedLanguage(lang)
										}
										className="language-button"
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="0.85rem"
											height="0.85rem"
											viewBox="0 0 32 32"
										>
											<path
												fill="currentColor"
												d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
											></path>
										</svg>
										<span
											style={{
												fontSize: "0.85rem",
												textTransform: "capitalize",
											}}
										>
											{getLanguageName(
												lang,
												i18n.language,
											)}
										</span>
									</div>
								)
							})}
						</div>
					</div>
				</div>
			</Popover>

			<Popover
				active={subscriptionPopoverActive}
				activator={<div></div>}
				onClose={() => setSubscriptionPopoverActive(false)}
			>
				<div style={{ width: "22rem", padding: "0.7rem" }}>
					<div style={{ fontSize: "0.95rem" }}>
						{t("ai.upgrade_to")}{" "}
						<span style={{ fontWeight: "600" }}>Kopy Plus</span>{" "}
						{t("ai.now_and_unlock_the_ability_to")}
					</div>

					<div
						style={{
							fontSize: "0.9rem",
							marginTop: "1rem",
							color: "#154cac",
						}}
					>
						<svg
							style={{ verticalAlign: "middle" }}
							xmlns="http://www.w3.org/2000/svg"
							width="1rem"
							height="1rem"
							viewBox="0 0 32 32"
						>
							<path
								fill="currentColor"
								d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
							></path>
						</svg>
						<span style={{ marginLeft: "0.5rem" }}>
							{t(
								"ai.translate_your_product_title_description_and_variants",
							)}
						</span>
					</div>
					<div
						style={{
							fontSize: "0.9rem",
							marginTop: "0.5rem",
							marginBottom: "1.5rem",
							color: "#7415ac",
						}}
					>
						<svg
							style={{ verticalAlign: "middle" }}
							xmlns="http://www.w3.org/2000/svg"
							width="1rem"
							height="1rem"
							viewBox="0 0 15 15"
						>
							<path
								fill="currentColor"
								fillRule="evenodd"
								d="M13.9.5a.4.4 0 0 0-.8 0v.6h-.6a.4.4 0 0 0 0 .8h.6v.6a.4.4 0 0 0 .8 0v-.6h.6a.4.4 0 0 0 0-.8h-.6V.5Zm-2.046 2.646a.5.5 0 0 1 0 .708l-1 1a.5.5 0 1 1-.707-.708l1-1a.5.5 0 0 1 .707 0Zm-2 2a.5.5 0 0 1 0 .708l-7 7a.5.5 0 1 1-.708-.708l7-7a.5.5 0 0 1 .708 0ZM13.5 5.1c.22 0 .4.18.4.4v.6h.6a.4.4 0 0 1 0 .8h-.6v.6a.4.4 0 0 1-.8 0v-.6h-.6a.4.4 0 0 1 0-.8h.6v-.6c0-.22.18-.4.4-.4ZM8.9.5a.4.4 0 0 0-.8 0v.6h-.6a.4.4 0 1 0 0 .8h.6v.6a.4.4 0 1 0 .8 0v-.6h.6a.4.4 0 0 0 0-.8h-.6V.5Z"
								clipRule="evenodd"
							></path>
						</svg>
						<span style={{ marginLeft: "0.5rem" }}>
							{t("ai.enhance_your_product_title_description")}
						</span>
					</div>

					<GlowingButtonContainer intensity="small">
						<CoolButton
							onClick={() => navigate("/upgradeplus")}
							fullWidth
						>
							{t("ai.upgrade_now")}
						</CoolButton>
					</GlowingButtonContainer>
				</div>
			</Popover>

			<style>{`
				.ai-orders-buttons-strip {
					filter: opacity(0.6) blur(1.5px);
					transition: filter 0.2s ease-in-out;
				}
				.ai-orders-buttons-strip:hover {
					filter: opacity(0.5) blur(0px);
				}

				.language-button {
					display: flex;
					gap: 0.3rem;
					align-items: center;
					padding: 0.2rem 0.3rem;
					cursor: pointer;
					background: none;
					border-radius: 0.3rem;
				}
				.language-button:hover {
					background: #e8e8e8;
				}
				.language-selector-icon:hover {
					border-radius: 0.25rem;
					background: #92bde7;
				}
			`}</style>
		</div>
	)
}
